import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { DriverNo } from "generated/sales";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { ITEM_VIEW } from "services/AgGrid/ItemAggrid";
import { IExpensesList } from "types/Logistic";
import {
  expenseItemsPrimaryConfig,
  expenseItemsSecondaryConfig,
  expensesListData,
  expensesListData2,
} from "components/Form/Logistic/DeliveryTrip/schema";

// 86d4b1jdw — the preset รายรับพนักงานขับรถ rows (ค่าเบี้ยเลี้ยง, ค่า Drop, ...)
// for one driver, built from the item master so item / uom ids are the
// environment's own, with the static rows in schema.ts as the fallback when
// the master cannot be read.
//
// This used to live in DocumentInfoTab as an effect that seeded both drivers
// at once, and only on a brand-new document. Since 5d667ec (Mar 2026) its
// deps no longer included the drivers, so it ran at mount and never again:
// pick a driver on a new document and the section opened empty; on a saved
// document it never seeded at all. 86d4889jb's whole premise is booking
// first and assigning the driver later, so "driver present, no rows" is now
// the common case rather than the odd one. `ExpensesList` seeds per driver
// from here whenever a driver has no rows and nothing to restore.
export const useExpensePresets = () => {
  // null = master not read yet; {} = read failed, use the static rows
  const [itemMap, setItemMap] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    const uids = Array.from(
      new Set(
        [...expenseItemsPrimaryConfig, ...expenseItemsSecondaryConfig].map(
          ({ uid }) => uid,
        ),
      ),
    );
    let cancelled = false;
    (async () => {
      try {
        const client = createGraphQLClientWithMiddleware("wms");
        const { ItemsViewAggrid } = await client.request(ITEM_VIEW, {
          aggridInput: {
            startRow: 0,
            endRow: uids.length,
            filterModel: {
              unique_id: { filterType: "set", values: uids },
            },
            sortModel: [],
          },
        });
        const map: Record<string, any> = {};
        for (const item of ItemsViewAggrid.results) {
          map[item.unique_id] = item;
        }
        if (!cancelled) setItemMap(map);
      } catch (_) {
        if (!cancelled) setItemMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildPresets = useCallback(
    (driverNo: DriverNo): IExpensesList[] => {
      const config =
        driverNo === DriverNo.Primary
          ? expenseItemsPrimaryConfig
          : expenseItemsSecondaryConfig;
      const fallback =
        driverNo === DriverNo.Primary ? expensesListData : expensesListData2;

      const fromMaster =
        itemMap && Object.keys(itemMap).length > 0
          ? config
              .map(({ uid, withholding_tax_type }) => {
                const item = itemMap[uid];
                if (!item) return null;
                const row: IExpensesList = {
                  unique_id: uuidv4(),
                  item_id: item.id || 0,
                  item_unique_id: item.unique_id || "",
                  item_name: item.name || "",
                  item_sku_desc: item.description,
                  qty: 1,
                  uom_id: item.stock_uom_id || 0,
                  uom_name: item.stock_uom_name || "",
                  uom_conversion_list: item.uom_conversion_list,
                  discount: 0,
                  pre_vat_amount: 0,
                  price_per_unit: 0,
                  withholding_tax_type,
                  withholding_tax_value: 0,
                  vat_percentage: "ไม่มี",
                  driver_no: driverNo,
                  net_amount: 0,
                  withholding_tax_amount: 0,
                  total_amount: 0,
                };
                return row;
              })
              .filter((row): row is IExpensesList => row !== null)
          : [];

      if (fromMaster.length > 0) return fromMaster;

      // The static rows share one module-level unique_id each; give every
      // seeded row its own so two documents never carry the same key.
      return fallback.map((row) => ({
        ...row,
        unique_id: uuidv4(),
        driver_no: driverNo,
      }));
    },
    [itemMap],
  );

  return { presetsReady: itemMap !== null, buildPresets };
};
